import React from 'react';
import Icon from './Icon';
import { SectionHead, ArrowButton } from './ui';
import { ParallaxImage } from '../motion/Parallax';
import { Reveal, Stagger, StaggerItem } from '../motion/Reveal';
import TiltCard from '../motion/TiltCard';
import { CONTRACTING, ENGAGEMENT_MODELS } from '../data/site';

/* Home page: photo cards for each contracting service (hover reveals the scope) */
export function ContractingGrid({ num = '03' }) {
  return (
    <section className="section ct-section">
      <div className="container">
        <div className="head-row">
          <SectionHead num={num} kicker="Contracting" title="We don't just manage —|we *build* it too."
            lead="Our own engineers and crews deliver residential, commercial and layout projects — executed with the same discipline we enforce as your PMC." />
          <ArrowButton to="/services" variant="outline">Contracting services</ArrowButton>
        </div>
        <Stagger className="ct-grid" gap={0.08}>
          {CONTRACTING.map((c, i) => (
            <StaggerItem key={c.title}>
              <article className="ct-card">
                <div className="ct-media">
                  <span className="ct-img"><img src={c.image} alt={c.title} loading="lazy" decoding="async" /></span>
                  <span className="ct-no">0{i + 1}</span>
                  <span className="ct-ico"><Icon name={c.icon} size={20} /></span>
                </div>
                <div className="ct-body">
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                  <ul className="ct-scope">
                    {c.scope.slice(0, 3).map((s) => <li key={s}><Icon name="check" size={14} />{s}</li>)}
                  </ul>
                </div>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* Services page: alternating image / detail rows for each contracting service */
export function ContractingRows({ num = '02' }) {
  return (
    <section className="section section-alt" id="contracting" style={{ scrollMarginTop: 60 }}>
      <div className="container">
        <SectionHead center num={num} kicker="Contracting services" title="Built by us,|*governed* like a PMC"
          lead="When you want one team to build it, our contracting division executes the work — and our PMC standards check every stage of it." />
        <div className="ct-rows">
          {CONTRACTING.map((c, i) => (
            <Reveal key={c.title} from={i % 2 ? 'right' : 'left'} className={`ct-row${i % 2 ? ' ct-flip' : ''}`}>
              <div className="ct-row-media">
                <ParallaxImage src={c.image} alt={c.title} speed={0.08} />
                <span className="ct-no">0{i + 1} / 0{CONTRACTING.length}</span>
              </div>
              <div className="ct-row-body">
                <div className="icon-box"><Icon name={c.icon} size={24} /></div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
                <ul className="check-list ct-list">{c.scope.map((s) => <li key={s}><Icon name="checkCircle" size={16} />{s}</li>)}</ul>
                <div className="tag-row">{c.tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Both pages: PMC only / Build only / PMC + Build */
export function EngagementModels({ num = '04', alt = false }) {
  return (
    <section className={`section${alt ? ' section-alt' : ''}`}>
      <div className="container">
        <SectionHead center num={num} kicker="How to engage us" title="Three ways to|work *with us*"
          lead="Choose independent project management, construction only, or both together under one accountable team." />
        <Stagger className="em-grid" gap={0.1}>
          {ENGAGEMENT_MODELS.map((m) => (
            <StaggerItem key={m.title}>
              <TiltCard className={`card em-card${m.featured ? ' featured' : ''}`} max={5}>
                <span className="em-tag">{m.tag}</span>
                <div className="icon-box"><Icon name={m.icon} size={24} /></div>
                <h3 className="h3">{m.title}</h3>
                <p>{m.desc}</p>
                <ul className="check-list em-list">{m.points.map((p) => <li key={p}><Icon name="check" size={15} />{p}</li>)}</ul>
                <ArrowButton to="/request-consultation" variant={m.featured ? 'white' : 'outline'} small magnetic={false}>Get a proposal</ArrowButton>
              </TiltCard>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}
